import api from './base/BaseApi';
import { handleApiError } from './base/handleApiError';
import { getChunkedBatchById } from './base/chunkedGet';
import { ResponseBody } from './types/ResponseBody';
import {
  TGetWalletsRequestParams,
  GetBalanceReponse,
  GetWalletResponse,
  TRsGetProofOfAccount,
  TRsGetBankStatement,
  TRsGetBankStatements,
  TRsBatchWalletBalance,
  TRsBatchWalletBalances
} from './types/WalletData';

const getWalletRequestParam = (options: TGetWalletsRequestParams) => {
  const urlParams = new URLSearchParams();
  if (options.accountId) {
    urlParams.append('accountId', options.accountId);
  }

  if (options.businessId) {
    urlParams.append('businessId', options.businessId);
  }

  return urlParams.toString();
};

export const getWallets = async (options: TGetWalletsRequestParams) =>
  api
    .get<GetWalletResponse[]>(`api/v1/wallets?${getWalletRequestParam(options)}`)
    .catch(handleApiError);

export const getWalletBalance = async (walletId: string) =>
  api.get<GetBalanceReponse>(`api/v1/wallets/${walletId}/balance`).catch(handleApiError);

// Fetches balances for multiple wallets in as few round trips as possible - one request
// for a typical wallet list, chunked into parallel requests for larger ones (chunk size
// defaults to getChunkedBatchById's own default; pass a size as the last arg to override).
// If one chunk's request fails, the others still resolve; only that chunk's wallets are
// reported as failed instead of losing the whole batch.
export const getWalletBalances = async (
  walletIds: string[]
): Promise<ResponseBody<TRsBatchWalletBalances>> =>
  getChunkedBatchById<TRsBatchWalletBalance>(
    'api/v1/wallets/balances',
    'walletIds',
    walletIds,
    () => ({
      error: 'fetch_failed'
    })
  );

export const getProofOfAccount = async (walletId: string) =>
  api
    .get<TRsGetProofOfAccount>(`api/v1/wallets/${walletId}/proof-of-account`)
    .catch(handleApiError);

export const getBankStatement = async (walletId: string, year: number, month: number) =>
  api
    .get<TRsGetBankStatement>(
      `api/v1/wallets/${walletId}/bank-statement?year=${year}&month=${month}`
    )
    .catch(handleApiError);

export const getBankStatements = async (walletId: string, statementBalancesMonthsUpTo: number) =>
  api
    .get<TRsGetBankStatements>(
      `api/v1/wallets/${walletId}/bank-statements?includeStatementBalances=true&statementBalancesMonthsUpTo=${statementBalancesMonthsUpTo}`
    )
    .catch(handleApiError);
